import { useEffect, useMemo, useState } from 'react'
import { useAuth } from 'context/authentication/useAuth'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useQuery } from '@tanstack/react-query'
import { CellContext } from '@tanstack/react-table'
import to from 'await-to-js'
import { DataTable, DataTableProvider } from '../DataTable'
import { IColumnVisibility } from '../DataTable/types'
import { DataTableContainer } from '../DataTable/UI'
import {
  listSampleRequests,
  useCancelSampleRequest,
  type ListSampleRequestsQueryParams,
} from '../../services/samples'
import { SampleRequest } from '../../models/samples'
import { getTableQueryKey } from '../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../DataTable/DataTableSorting/DataTableSortingStore'
import { ActionsDropdown } from '../Buttons/ActionsDropdown'
import { toast } from 'react-toastify'
import { FbLink } from '../FbUI/FbLink'
import { ExportButton } from '../Buttons/ExportButton'
import { format } from 'date-fns'
import { formatDateOnly } from '../../utils/formatting'
import { ColumnsStoreProvider } from '../../stores/ColumnsStore/ColumnsStoreProvider'
import { filterStoreRepo, useFilterParams } from '../Filters/FilterStore'
import { FilterIdentifier } from '../../models/saved_view'
import { TableSearch } from '../Filters/TableSearch'
import { FilterChips } from '../Filters/components/FilterChips/FilterChips'
import * as S from '../Tables/CommonTable.styles'
import { useTableLinkVariant } from '../Tables/CommonTableCells/TableLinkStyleContext'
import { InitialsAvatar } from '../Tables/CommonTableCells/InitialsAvatar'
import { SamplesFilterset } from './SamplesFilterset'
import { ColumnSelectorRecipient } from '../Modals/ColumnModal/ColumnModal'
import { SampleRequestPreviewModal } from './SampleRequestPreviewModal'
import SampleRequestExportModal from './SampleRequestExportModal'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { PiDownloadSimple, PiTruck } from 'react-icons/pi'
import { CancelSampleRequestModal } from './CancelSampleRequestModal'
import { SampleRequestReviewModal } from './SampleRequestReviewModal'
import { EditableSampleStatusCell } from './EditableSampleStatusCell'
import { SampleTrackingCell } from './SampleTrackingCell'
import {
  SampleTrackingModal,
  type TrackingModalSample,
} from './SampleTrackingModal'
import { ClipboardCheck, Trash2 } from 'lucide-react'
// SampleStatusBadge is rendered inside EditableSampleStatusCell now.
import { cn } from '../UI/cn'
import { PillListCell } from '../Tables/CommonTableCells/PillListCell'
import { PillOverflowChip } from '../Tables/CommonTableCells/PillOverflowChip'
import { OverflownText } from '../OverflownText'
import { TruncatedTextCell } from '../Tables/CommonTableCells/TruncatedTextCell'
import { TruncatedLinkCell } from '../Tables/CommonTableCells/TruncatedLinkCell'

const PAGE_SIZE = 50

const tooltip = <Tooltip id="tooltip">Download limited to 10,000 rows</Tooltip>

// FIR-7033: statuses from which a standard/admin user can set the manual
// fulfillment stage + tracking. Excludes pending_review (use Review),
// rejected, and cancelled — those are owned by the review/cancel flows.
const FULFILLMENT_EDITABLE_STATUSES = new Set<SampleRequest['status']>([
  'submitted',
  'accepted',
  'sent',
  'delivered',
])

// FIR-7033: whether a standard/admin user can edit fulfillment (status +
// tracking) on this sample. Mirrors the server-side FULFILLABLE_SOURCE_STATUSES
// gate in UpdateSampleFulfillmentUsecase. Single source of truth for the status
// cell, tracking cell, and row actions so they can't drift.
function canEditFulfillment(
  sample: SampleRequest,
  isLimited: boolean
): boolean {
  return !isLimited && FULFILLMENT_EDITABLE_STATUSES.has(sample.status)
}

const SHIPPING_COLUMNS = [
  { id: 'ship_to_name', header: 'Ship-To Name', size: 180 },
  { id: 'contact_email', header: 'Ship-To Email', size: 220 },
  { id: 'contact_phone', header: 'Ship-To Phone', size: 140 },
  { id: 'shipping_address_line1', header: 'Ship-To Address Line 1', size: 220 },
  { id: 'shipping_address_line2', header: 'Ship-To Address Line 2', size: 180 },
  { id: 'shipping_city', header: 'Ship-To City', size: 140 },
  { id: 'shipping_state', header: 'Ship-To State', size: 100 },
  { id: 'shipping_zip', header: 'Ship-To Zip', size: 110 },
  { id: 'shipping_country', header: 'Ship-To Country', size: 130 },
] as const

interface SamplesTableProps {
  setTotalRowsCount?: (count: number) => void
  onEditRequest?: (sampleId: number) => void
  onPreviewRequest?: (sampleId: number) => void
  tableKey: string
  myRequestsOnly?: boolean
  statusFilter?: string
  needsMyReview?: boolean
  /** Include inactive (cancelled/closed) samples. Lifted to the page so the
   *  toggle can live in the tabs row instead of the table toolbar. */
  showInactive?: boolean
}

function SamplesTableComponent({
  setTotalRowsCount,
  onPreviewRequest,
  myRequestsOnly,
  statusFilter,
  needsMyReview,
  tableKey,
  showInactive = false,
}: SamplesTableProps) {
  const { isLimited } = useAuth()
  // FIR-7033 is gated behind `enable_sample_tracking` so it can be sold per
  // account. When off, the samples table reverts to its pre-7033 state: the
  // status is read-only (no Sent/Delivered editor) and the tracking columns,
  // tracking editor, and export tracking group are hidden. Mirrors the
  // server-side gate in UpdateSampleFulfillmentUsecase (SAMPLE_TRACKING_FEATURE_FLAG).
  const featureFlags = useFeatureFlagService()
  const sampleTrackingEnabled = Boolean(featureFlags.enableSampleTracking)
  const linkVariant = useTableLinkVariant()
  // Use different filter identifier for each table to avoid filter sharing
  const IDENTIFIER = (
    statusFilter
      ? 'NEEDS_REVIEW_SAMPLES_TABLE'
      : myRequestsOnly
        ? 'MY_SAMPLES_TABLE'
        : 'SAMPLES_TABLE'
  ) as FilterIdentifier
  const samplesFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const { page, setPage } = samplesFilterStore()
  const filterParams = useFilterParams(samplesFilterStore)
  const sortingStore = dataTableSortingStoreRepo.getStore(tableKey)
  const { sorting, setSorting } = sortingStore()
  const [previewRequestId, setPreviewRequestId] = useState<number | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)

  // Set default sort to created (input date) descending on initial load
  useEffect(() => {
    if (!sorting || sorting.length === 0) {
      setSorting([{ id: 'created', desc: true }])
    }
  }, [])

  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelRequestData, setCancelRequestData] = useState<{
    id: number
    requestNumber: string
  } | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [reviewRequestId, setReviewRequestId] = useState<number | null>(null)
  // FIR-7033: sample whose tracking info is being edited (null = modal closed).
  const [trackingSample, setTrackingSample] =
    useState<TrackingModalSample | null>(null)

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['ordering'] = sorting[0].desc
        ? '-' + sorting[0]?.id
        : sorting[0]?.id
    }
    return params
  }, [sorting])

  const filterAndSortParams = useMemo(() => {
    const params = {
      ...filterParams,
      ...sortParams,
    }

    // Add status exclusion when showInactive is false
    // BUT only if there's no explicit status filter already set
    const hasStatusFilter =
      filterParams.status &&
      (Array.isArray(filterParams.status)
        ? filterParams.status.length > 0
        : true)
    if (!showInactive && !hasStatusFilter) {
      params['status__exclude'] = ['cancelled']
    }

    return params
  }, [filterParams, sortParams, showInactive])

  const allFilterParams = useMemo(() => {
    return {
      ...filterAndSortParams,
      ...(myRequestsOnly ? { my_requests_only: true } : {}),
      ...(statusFilter ? { status: statusFilter as any } : {}),
      ...(needsMyReview ? { needs_my_review: true } : {}),
    }
  }, [filterAndSortParams, myRequestsOnly, statusFilter, needsMyReview])

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey,
    filterParams: allFilterParams,
    page,
  })

  const cancelMutation = useCancelSampleRequest()

  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async () => {
      return await listSampleRequests({
        ...allFilterParams,
        page,
        page_size: PAGE_SIZE,
      } as ListSampleRequestsQueryParams)
    },
  })

  useEffect(() => {
    setTotalRowsCount?.(data?.count ?? 0)
  }, [data?.count])

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return {
        date: format(date, 'MM/dd/yyyy'),
        time: format(date, 'h:mm a'),
      }
    } catch {
      return { date: dateString, time: '' }
    }
  }

  const handleCancelClick = (sampleId: number, requestNumber: string) => {
    setCancelRequestData({ id: sampleId, requestNumber })
    setCancelModalOpen(true)
  }

  // FIR-7033: open the tracking editor prefilled from a table row.
  const openTrackingModal = (sample: SampleRequest) => {
    setTrackingSample({
      id: sample.id,
      request_number: sample.request_number,
      tracking_number: sample.tracking_number,
      tracking_carrier: sample.tracking_carrier,
      shipped_date: sample.shipped_date,
    })
  }

  const handleCancelConfirm = async (reason: string) => {
    if (!cancelRequestData) return

    setIsCancelling(true)
    const [err] = await to(
      cancelMutation.mutateAsync({
        id: cancelRequestData.id,
        data: { cancellation_reason: reason },
      })
    )

    setIsCancelling(false)

    if (!err) {
      toast.success('Sample request cancelled successfully')
      setCancelModalOpen(false)
      setCancelRequestData(null)
      void refetch()
    } else {
      toast.error('Failed to cancel sample request')
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status_display',
        cell: ({ row }: CellContext<SampleRequest, unknown>) => (
          <EditableSampleStatusCell
            sampleId={row.original.id}
            status={row.original.status}
            statusDisplay={row.original.status_display}
            // Standard/admin can set the manual fulfillment stage on a
            // fulfillable sample; review/cancel states keep their own flows.
            // Gated off entirely when the sample-tracking flag is disabled.
            editable={
              sampleTrackingEnabled &&
              canEditFulfillment(row.original, isLimited)
            }
          />
        ),
        size: 160,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'fulfillment_option_display',
        header: 'Fulfillment Option',
        accessorKey: 'fulfillment_option_display',
        size: 180,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'request_number',
        header: 'Request #',
        accessorKey: 'request_number',
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              <FbLink
                to={`?sample_request_id=${row.original.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  if (onPreviewRequest) {
                    onPreviewRequest(row.original.id)
                  } else {
                    setPreviewRequestId(row.original.id)
                  }
                }}
                style={{ cursor: 'pointer', fontWeight: 'bold' }}
              >
                {row.original.request_number}
              </FbLink>
            </div>
          )
        },
        size: 120,
        meta: {
          headerGroup: 'Order Details',
          isSticky: true,
          isFirstColumn: true,
          isEditable: false,
        },
      },
      {
        id: 'created',
        header: 'Created',
        accessorKey: 'created',
        cell: ({ getValue, row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          const { date, time } = formatDateTime(getValue() as string)
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              {/* text-sm ≈ 12px at the app's 14px root (text-xs would be ~11px) */}
              <div className="text-sm">{date}</div>
              <div className="text-xs text-gray-500">{time}</div>
            </div>
          )
        },
        size: 120,
        meta: {
          headerGroup: 'Order Details',
          // Renders date over time — opt out of single-line truncation.
          multiLine: true,
        },
      },
      {
        id: 'date_needed',
        header: 'Need by',
        accessorKey: 'date_needed',
        cell: ({ getValue, row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              {formatDateOnly(getValue() as string)}
            </div>
          )
        },
        size: 120,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'user_name',
        header: 'Requestor',
        accessorKey: 'user_name',
        cell: ({ getValue, row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          const name = (getValue() as string | null) ?? ''
          if (!name) return <>—</>
          const initials = name
            .split(' ')
            .map((n) => n[0] ?? '')
            .join('')
            .toUpperCase()
            .slice(0, 2)
          return (
            <div
              className={cn(
                'flex items-center gap-2.5',
                isCancelled && 'opacity-60'
              )}
            >
              <InitialsAvatar initials={initials} />
              <span className="truncate">{name}</span>
            </div>
          )
        },
        size: 220,
        meta: {
          headerGroup: 'Requestor Info',
        },
      },
      {
        id: 'user_email',
        header: 'Requestor Email',
        accessorKey: 'user_email',
        cell: ({ getValue, row }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              {getValue() as string}
            </div>
          )
        },
        size: 200,
        meta: {
          headerGroup: 'Requestor Info',
        },
      },
      {
        id: 'company_name',
        header: 'Company',
        accessorKey: 'company_name',
        size: 250,
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          const name = row.original.company_name ?? ''
          if (
            !row.original.company_id ||
            !row.original.company_notification_link
          ) {
            return name ? <TruncatedTextCell text={name} /> : '-'
          }
          return (
            <TruncatedLinkCell
              text={name}
              to={row.original.company_notification_link}
            />
          )
        },
        meta: {
          headerGroup: 'Requestor Info',
        },
      },
      {
        id: 'location_name',
        header: 'Location',
        accessorKey: 'location_name',
        size: 250,
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          if (
            !row.original.location_id ||
            !row.original.location_notification_link
          ) {
            return '-'
          }
          return (
            <FbLink to={row.original.location_notification_link}>
              {row.original.location_name}
            </FbLink>
          )
        },
        meta: {
          headerGroup: 'Order Details',
        },
      },
      {
        id: 'line_items',
        header: 'Products',
        accessorKey: 'line_items',
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          const items = row.original.line_items || []
          const isCancelled = row.original.status === 'cancelled'
          if (items.length === 0)
            return <div className={cn(isCancelled && 'opacity-60')}>-</div>

          const productNames = items.map(
            (item) => `${item.product_name} - ${item.variant_name}`
          )

          if (linkVariant === 'plain') {
            // DS2026: one truncated pill + clickable "+N" chip popover for the rest.
            const [first, ...rest] = productNames
            return (
              <div
                className={cn(
                  'flex min-w-0 max-w-full items-center gap-1',
                  isCancelled && 'opacity-60'
                )}
              >
                <span className="inline-flex min-w-0 items-center px-2.5 py-0.5 rounded-2xl text-sm font-medium leading-5 tracking-[-0.2px] bg-gray-100 text-gray-700">
                  <OverflownText tooltipText={first} placement="bottom">
                    {first}
                  </OverflownText>
                </span>
                {rest.length > 0 && (
                  <span className="shrink-0">
                    <PillOverflowChip count={rest.length}>
                      {rest.map((name, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-2xl text-sm font-medium leading-5 tracking-[-0.2px] bg-gray-100 text-gray-700"
                        >
                          {name}
                        </span>
                      ))}
                    </PillOverflowChip>
                  </span>
                )}
              </div>
            )
          }

          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              <PillListCell
                items={productNames}
                maxColumnWidth={400}
                maxPillWidth={180}
              />
            </div>
          )
        },
        size: 400,
        meta: {
          headerGroup: 'Order Details',
        },
      },
      ...SHIPPING_COLUMNS.map(({ id, header, size }) => ({
        id,
        header,
        accessorKey: id,
        size,
        cell: ({ row, getValue }: CellContext<SampleRequest, unknown>) => {
          const isCancelled = row.original.status === 'cancelled'
          const value = (getValue() as string | undefined) ?? ''
          return (
            <div className={cn(isCancelled && 'opacity-60')}>
              {value || '-'}
            </div>
          )
        },
        meta: {
          headerGroup: 'Shipping',
        },
      })),
      // FIR-7033 Fulfillment columns (tracking #, carrier, shipped date) are
      // gated behind the sample-tracking flag.
      ...(sampleTrackingEnabled
        ? [
            {
              id: 'tracking_number',
              header: 'Tracking #',
              accessorKey: 'tracking_number',
              size: 200,
              cell: ({ row }: CellContext<SampleRequest, unknown>) => (
                <SampleTrackingCell
                  sample={row.original}
                  canEdit={canEditFulfillment(row.original, isLimited)}
                  onEdit={openTrackingModal}
                />
              ),
              meta: {
                headerGroup: 'Fulfillment',
              },
            },
            {
              id: 'tracking_carrier_display',
              header: 'Carrier',
              accessorKey: 'tracking_carrier_display',
              size: 130,
              cell: ({ getValue }: CellContext<SampleRequest, unknown>) => {
                const value = (getValue() as string | undefined) ?? ''
                return <div>{value || '-'}</div>
              },
              meta: {
                headerGroup: 'Fulfillment',
              },
            },
            {
              id: 'shipped_date',
              header: 'Shipped',
              accessorKey: 'shipped_date',
              size: 120,
              cell: ({ getValue }: CellContext<SampleRequest, unknown>) => {
                const value = getValue() as string | null | undefined
                return <div>{value ? formatDateOnly(value) : '-'}</div>
              },
              meta: {
                headerGroup: 'Fulfillment',
              },
            },
          ]
        : []),
      {
        id: 'actions',
        header:
          linkVariant === 'plain'
            ? () => <div className="w-full text-center">Actions</div>
            : 'Actions',
        size: linkVariant === 'plain' ? 70 : 80,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          rightAlign: linkVariant !== 'plain',
          isLastColumn: true, // Sticky on DS2026 surfaces, free-floating elsewhere (gated via preventLastColumnSticky based on linkVariant)
        },
        cell: ({ row }: CellContext<SampleRequest, unknown>) => {
          const sample = row.original
          // Limited users see only their own samples (enforced server-side)
          // and cannot cancel once accepted. Admin/standard can cancel any
          // non-cancelled sample.
          const canCancel = isLimited
            ? sample.status !== 'accepted' && sample.status !== 'cancelled'
            : sample.status !== 'cancelled'
          const canReview = sample.status === 'pending_review' && !isLimited
          // FIR-7033: standard/admin can edit tracking on fulfillable samples,
          // when the sample-tracking flag is enabled.
          const canEditTracking =
            sampleTrackingEnabled && canEditFulfillment(sample, isLimited)

          const actions = [
            ...(canReview
              ? [
                  {
                    label: 'Review',
                    icon: ClipboardCheck,
                    onClick: () => setReviewRequestId(sample.id),
                  },
                ]
              : []),
            ...(canEditTracking
              ? [
                  {
                    label: 'Edit tracking',
                    icon: PiTruck,
                    onClick: () => openTrackingModal(sample),
                  },
                ]
              : []),
            {
              label: 'Delete',
              icon: Trash2,
              onClick: () =>
                handleCancelClick(sample.id, sample.request_number),
              disabled: !canCancel,
              variant: 'destructive' as const,
            },
          ]

          return (
            <ActionsDropdown
              actions={actions}
              className={
                linkVariant === 'plain' ? 'justify-center' : 'justify-end'
              }
            />
          )
        },
      },
    ],
    [onPreviewRequest, isLimited, linkVariant, sampleTrackingEnabled]
  )

  return (
    <>
      <DataTableContainer>
        <DataTable
          tableHeader={
            <S.SearchContainer>
              <div className="flex w-full gap-2 items-center">
                <TableSearch
                  className={'flex-1'}
                  filterStore={samplesFilterStore}
                  searchPlaceholder="Search by request number, company, or tracking #..."
                  tableKey={tableKey}
                />
                <SamplesFilterset filterIdentifier={IDENTIFIER} />
                <ColumnSelectorRecipient tableKey={tableKey} />
              </div>
              <FilterChips
                identifier={IDENTIFIER}
                store={samplesFilterStore}
                clearAllButton
                showActive
              />
            </S.SearchContainer>
          }
          loading={isFetching}
          tableKey={tableKey}
          data={(data?.results ?? []) as unknown as SampleRequest[]}
          columns={columns as any}
          sortableFields={[
            'request_number',
            'created',
            'date_needed',
            'user_name',
            'user_email',
            'company_name',
            'location_name', // Added for FIR-4061
            'status',
            // FIR-7033 tracking sorts only exist when the flag is enabled.
            ...(sampleTrackingEnabled
              ? ['tracking_number', 'shipped_date']
              : []),
          ]}
          virtualizeRows
          enableColumnResizing={linkVariant === 'plain'}
          rowHeight={linkVariant === 'plain' ? 44 : undefined}
          headerHeight={linkVariant === 'plain' ? 40 : undefined}
          isPaginationEnabled
          preventLastColumnSticky={linkVariant !== 'plain'}
          paginationOptions={{
            totalRows: data?.count ?? 0,
            pageSize: PAGE_SIZE,
            setPage,
            page,
            isPaginationLoading: isFetching,
          }}
          emptyTableText={{
            title: 'No sample requests found',
            subtitle:
              'Try adjusting your filters or create a new sample request',
          }}
          footerControls={
            <OverlayTrigger placement="top" overlay={tooltip}>
              <ExportButton
                variant="secondary"
                className={
                  linkVariant === 'plain'
                    ? '!text-neutral-800 !border-neutral-300 hover:!bg-neutral-100 hover:!text-neutral-900 hover:!border-neutral-300 hover:!shadow-none'
                    : undefined
                }
                onClick={() => setShowExportModal(true)}
              >
                <PiDownloadSimple size={18} />
              </ExportButton>
            </OverlayTrigger>
          }
        />
      </DataTableContainer>

      {previewRequestId && (
        <SampleRequestPreviewModal
          show={!!previewRequestId}
          onHide={() => setPreviewRequestId(null)}
          sampleRequestId={previewRequestId}
        />
      )}

      {showExportModal && (
        <SampleRequestExportModal
          show={showExportModal}
          handleClose={() => setShowExportModal(false)}
          filters={allFilterParams}
        />
      )}
      <CancelSampleRequestModal
        open={cancelModalOpen}
        onOpenChange={setCancelModalOpen}
        onConfirm={handleCancelConfirm}
        isSubmitting={isCancelling}
        requestNumber={cancelRequestData?.requestNumber}
      />

      <SampleRequestReviewModal
        show={!!reviewRequestId}
        onHide={() => setReviewRequestId(null)}
        sampleRequestId={reviewRequestId}
        onSuccess={() => void refetch()}
      />

      {/* FIR-7033: key remounts the form so its fields reset per sample.
          The fulfillment mutation invalidates the table cache on success.
          Only mounted when the sample-tracking flag is enabled. */}
      {sampleTrackingEnabled && (
        <SampleTrackingModal
          key={trackingSample?.id ?? 'none'}
          open={!!trackingSample}
          onOpenChange={(open) => !open && setTrackingSample(null)}
          sample={trackingSample}
        />
      )}
    </>
  )
}

export function SamplesTable(
  props: SamplesTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  // Define default column visibility and order (FIR-4061: user_name leftmost, status rightmost)
  const defaultVisibility = defaultColumnVisibility || {
    user_name: true,
    company_name: true,
    location_name: false, // Hidden by default per FIR-4061 feedback
    request_number: true,
    created: true,
    date_needed: true,
    user_email: false, // Hidden by default per FIR-4061 feedback
    line_items: true,
    status: true,
    fulfillment_option_display: false, // Hidden by default per FIR-4061 feedback
    ...Object.fromEntries(SHIPPING_COLUMNS.map((c) => [c.id, false])),
    // FIR-7033: tracking # visible by default; carrier/shipped opt-in.
    tracking_number: true,
    tracking_carrier_display: false,
    shipped_date: false,
    actions: true,
  }

  const initialColumnOrder = [
    'request_number', // Leftmost and sticky
    'company_name',
    'location_name',
    'user_name', // Requestor/Operator Name
    'created',
    'date_needed',
    'user_email',
    'line_items',
    'status',
    'tracking_number', // FIR-7033
    'tracking_carrier_display', // FIR-7033
    'shipped_date', // FIR-7033
    'fulfillment_option_display',
    ...SHIPPING_COLUMNS.map((c) => c.id),
    'actions', // Always last (not sticky)
  ]

  const preferredGroupOrder = [
    'Order Details',
    'Fulfillment', // FIR-7033
    'Requestor Info',
    'Shipping',
    'Actions',
  ]

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      savedPresetsIdentifier={'SAMPLES_TABLE' as FilterIdentifier}
      defaultColumnVisibility={defaultVisibility}
      initialColumnOrder={initialColumnOrder}
      preferredGroupOrder={preferredGroupOrder}
    >
      <DataTableProvider tableKey={tableKey}>
        <SamplesTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
